import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { authenticate } from '../../functions/api';
import { useLogin } from '../../hooks/useLogin';
import LoginRequest from '../../models/LoginRequest';
import { modalPatientMassUploadConfirmationConfig } from '../Patient/models/Patient.models';
import { ResetPasswordResponseEnum } from './components/models/ResetPassword.models';
import { ResetPasswordForm } from './components/ResetPasswordForm';
import { ResetPasswordService } from './services/ResetPassword.service';
import './styles/Login.scss';
import { useDataContext } from '../../contexts/DataContext'
import { useMaintenance } from '../../hooks/useMaintenance';

const emptyLoginRequest: LoginRequest = {
    username: '',
    password: '',
};

const Login = () => {
    const [loginRequest, setLoginRequest] = useState<LoginRequest>(emptyLoginRequest);
    const [wrongData, setWrongData] = useState(false);
    const { t } = useTranslation();
    const { login } = useLogin();
    const { isOnMaintenance } = useDataContext();
    const { maintenance } = useMaintenance();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setLoginRequest({ ...loginRequest, [id]: value });
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Client validation.
        if (loginRequest.username === '' || loginRequest.password === '') {
            setWrongData(true);
            return;
        }

        // Check maintenance mode with exceptions
        if ( maintenance.isScheduled ) {
            const maintenanceExceptions = ['victoradmin2', 'democlient', 'jorge'];
            if (isOnMaintenance && !maintenanceExceptions.includes(loginRequest.username)) {
                toast.error(t('label.maintenance'));
                return;
            }
        }

        // Server authentication.
        const loginResponse = await authenticate(loginRequest);

        if (loginResponse) {
            login(loginResponse);
        } else {
            setWrongData(true);
        }
    };

    //reset password
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

    const onClickResetPassword = () => {
        setShowResetPasswordModal(true);
    };

    const resetPasswordService = new ResetPasswordService();

    const onConfirmResetPassword = async (usernameOrMail: string) => {
        const response = await resetPasswordService.resetPasswordForUser(usernameOrMail);
        if (response.status === ResetPasswordResponseEnum.SUCCESS) {
            // Handle success
            toast.success(t('reset_password.password_reset_success'));
            setShowResetPasswordModal(false);
            //Handle not foun user
        } else if (response.status === ResetPasswordResponseEnum.NOT_FOUND) {
            toast.error(t('error.password_reset_not_found_user'));
            //Handle other errors
        } else {
            toast.error(t('error.password_reset_failed'));
        }
    };

    return (
        <>
            <section className="login-component">
                <form onSubmit={onSubmit}>
                    <div className="form-box">
                        <label htmlFor="username">{t('login.username')}</label>
                        <input
                            className="login-input"
                            type="text"
                            id="username"
                            name="username"
                            autoComplete="false"
                            value={loginRequest.username}
                            onChange={handleChange}
                        />

                        <label htmlFor="password">{t('login.password')}</label>
                        <input
                            className="login-input"
                            type="password"
                            id="password"
                            name="password"
                            autoComplete="current-password"
                            value={loginRequest.password}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="bottom-buttons">
                        <button type="submit">{t('login.sign_in')}</button>
                        <span
                            className="has-to-reset-password"
                            onClick={onClickResetPassword}
                        >
                            {t('password.has_to_reset_password')}
                        </span>
                    </div>
                </form>

                {wrongData && (
                    <div className="wrong-login">
                        <p>{t('login.incorrect')}</p>
                    </div>
                )}

                <Modal
                    isOpen={showResetPasswordModal}
                    onRequestClose={() => setShowResetPasswordModal(false)}
                    style={modalPatientMassUploadConfirmationConfig}
                    contentLabel="Reset Password"
                >
                    <ResetPasswordForm
                        onConfirm={onConfirmResetPassword}
                        onClose={() => setShowResetPasswordModal(false)}
                    />
                </Modal>
            </section>
        </>
    );
};

export default Login;
